import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, MessageSquare, Send, Globe, ArrowLeft, Check, X } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import API from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const LOGO = process.env.REACT_APP_LOGO_URL || '/logo.png';

// ── GOOGLE AUTH BUTTON ──
function GoogleButton({ loading, onClick, text }) {
  return (
    <button type="button" onClick={onClick} disabled={loading} data-testid="google-login-btn"
      className="w-full flex items-center justify-center gap-3 border border-[#2a2a2a] bg-white/[0.03] hover:bg-white/[0.06] hover:border-[#3a3a3a] text-white font-rajdhani font-semibold text-sm py-3 rounded-sm transition-all duration-200 disabled:opacity-50">
      <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
      {loading ? 'Connecting...' : text}
    </button>
  );
}

function Divider() {
  return (
    <div className="flex items-center gap-3 my-5">
      <div className="flex-1 h-[1px] bg-[#1e1e1e]" />
      <span className="text-xs text-zinc-700 font-rajdhani uppercase tracking-widest">or</span>
      <div className="flex-1 h-[1px] bg-[#1e1e1e]" />
    </div>
  );
}

// ── GOOGLE COMPLETION MODAL ──
function GoogleCompleteModal({ pendingData, onComplete, onCancel }) {
  const [username, setUsername] = useState(pendingData.suggested_username || '');
  const [role, setRole] = useState('');
  const [contacts, setContacts] = useState([{ type: 'discord', value: '' }]);
  const [loading, setLoading] = useState(false);
  const [usernameCheck, setUsernameCheck] = useState(null); // null | 'checking' | 'available' | 'taken'
  const checkTimeout = useRef(null);

  const checkUsername = (val, currentRole) => {
    if (checkTimeout.current) clearTimeout(checkTimeout.current);
    if (!val || val.length < 3 || currentRole !== 'seller') { setUsernameCheck(null); return; }
    setUsernameCheck('checking');
    checkTimeout.current = setTimeout(async () => {
      try {
        const { data } = await API.get(`/auth/check-username?username=${encodeURIComponent(val)}&role=seller`);
        setUsernameCheck(data.available ? 'available' : 'taken');
      } catch { setUsernameCheck(null); }
    }, 400);
  };

  const handleUsernameChange = (val) => {
    setUsername(val);
    checkUsername(val, role);
  };

  const handleRoleChange = (r) => {
    setRole(r);
    checkUsername(username, r);
  };

  const updateContact = (type, val) => {
    const existing = contacts.findIndex(c => c.type === type);
    if (existing >= 0) {
      const updated = [...contacts];
      if (!val.trim()) updated.splice(existing, 1);
      else updated[existing] = { type, value: val };
      setContacts(updated.length ? updated : [{ type: 'discord', value: '' }]);
    } else if (val.trim()) {
      setContacts(prev => [...prev, { type, value: val }]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!role) return toast.error('Please select account type');
    if (!username.trim() || username.trim().length < 3) return toast.error('Username must be at least 3 characters');
    if (role === 'seller') {
      if (usernameCheck === 'taken') return toast.error('Username already taken. Please choose another.');
      const validContacts = contacts.filter(c => c.value.trim());
      if (validContacts.length === 0) return toast.error('Sellers must provide at least one contact method');
    }
    setLoading(true);
    try {
      const { data } = await API.post('/auth/google-complete', {
        pending_token: pendingData.pending_token,
        username: username.trim(),
        role,
        contact_methods: role === 'seller' ? contacts.filter(c => c.value.trim()) : []
      });
      onComplete(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete signup');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="google-complete-modal">
      <div className="w-full max-w-md bg-[#0d0d0d] border border-[#1e1e1e] rounded-sm p-6 animate-scale-in my-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          {pendingData.picture && (
            <img src={pendingData.picture} alt="" className="w-10 h-10 rounded-full border border-[#2a2a2a]" />
          )}
          <div>
            <h2 className="font-rajdhani font-bold text-xl uppercase tracking-wide text-white">Complete Your Profile</h2>
            <p className="text-xs text-zinc-500">{pendingData.email}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="google-complete-form">
          {/* Username */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-2">Username</label>
            <div className="relative">
              <input
                type="text"
                value={username}
                onChange={e => handleUsernameChange(e.target.value)}
                placeholder="your_username"
                data-testid="complete-username"
                className="w-full bg-[#0a0a0a] border border-[#222] text-white placeholder:text-zinc-700 px-4 py-3 rounded-sm text-sm pr-10"
              />
              {role === 'seller' && username.length >= 3 && (
                <div className="absolute right-3 top-3.5">
                  {usernameCheck === 'checking' && <div className="w-4 h-4 border-2 border-zinc-600 border-t-transparent rounded-full animate-spin" />}
                  {usernameCheck === 'available' && <Check size={14} className="text-green-500" />}
                  {usernameCheck === 'taken' && <X size={14} className="text-rm-red" />}
                </div>
              )}
            </div>
            {role === 'seller' && usernameCheck === 'taken' && (
              <p className="text-[10px] text-rm-red mt-1">Username taken. Choose another.</p>
            )}
            {role === 'seller' && usernameCheck === 'available' && (
              <p className="text-[10px] text-green-500 mt-1">Username available!</p>
            )}
            {role !== 'seller' && (
              <p className="text-[10px] text-zinc-600 mt-1">Buyers can have any username (not required to be unique)</p>
            )}
          </div>

          {/* Account Type */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-2">
              Account Type <span className="text-rm-red">*</span>
            </label>
            {!role && (
              <p className="text-[10px] text-amber-500/80 mb-2 flex items-center gap-1"><span>▸</span> Please choose one to continue</p>
            )}
            <div className="grid grid-cols-2 gap-3">
              {['buyer', 'seller'].map(r => (
                <button key={r} type="button" data-testid={`complete-role-${r}`}
                  onClick={() => handleRoleChange(r)}
                  className={`py-3 border rounded-sm font-rajdhani font-bold uppercase text-sm transition-all
                    ${role === r
                      ? 'border-rm-red text-rm-red bg-rm-red/10'
                      : !role
                        ? 'border-amber-500/40 text-zinc-500 hover:border-amber-500/70 hover:text-zinc-300'
                        : 'border-[#222] text-zinc-600 hover:border-[#333] hover:text-zinc-400'}`}>
                  {r === 'buyer' ? 'Buyer' : 'Seller'}
                </button>
              ))}
            </div>
            {role === 'buyer' && (
              <p className="text-[10px] text-zinc-600 mt-2">Buyers can browse and contact sellers</p>
            )}
            {role === 'seller' && (
              <p className="text-[10px] text-zinc-600 mt-2">Sellers can list items for sale. Username must be unique.</p>
            )}
          </div>

          {/* Seller contact methods */}
          {role === 'seller' && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 block mb-1">Contact Methods</label>
              <p className="text-[10px] text-amber-500/80 mb-3">Fill in at least one — buyers will use this to reach you</p>
              <div className="space-y-2">
                {[
                  { type: 'discord',  icon: MessageSquare, placeholder: 'YourName#1234 or discord.gg/...', required: true },
                  { type: 'telegram', icon: Send,          placeholder: '@YourTelegram',                   required: true },
                  { type: 'email',    icon: Mail,          placeholder: 'contact@email.com',               required: false },
                  { type: 'website',  icon: Globe,         placeholder: 'https://yoursite.com',            required: false },
                ].map(({ type, icon: Icon, placeholder, required }) => (
                  <div key={type} className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5 w-28 shrink-0">
                      <Icon size={13} className={required ? 'text-rm-red/70' : 'text-zinc-600'} />
                      <span className={`text-xs capitalize ${required ? 'text-zinc-400' : 'text-zinc-600'}`}>{type}</span>
                      {required ? <span className="text-[9px] text-rm-red/60 font-bold">REQ</span> : <span className="text-[9px] text-zinc-700">opt</span>}
                    </div>
                    <input type="text" placeholder={placeholder}
                      value={contacts.find(c => c.type === type)?.value || ''}
                      onChange={e => updateContact(type, e.target.value)}
                      className="flex-1 bg-[#0a0a0a] border border-[#222] text-white placeholder:text-zinc-700 px-3 py-2 rounded-sm text-xs" />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onCancel} data-testid="complete-cancel-btn"
              className="flex-1 btn-outline py-3 rounded-sm text-sm">Cancel</button>
            <button type="submit" disabled={loading || !role || (role === 'seller' && usernameCheck === 'taken')}
              data-testid="complete-submit-btn"
              className="flex-1 btn-primary py-3 rounded-sm text-sm disabled:opacity-50">
              {loading ? 'Creating...' : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Auth() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') === 'register' ? 'register' : 'login');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [regData, setRegData] = useState({ username: '', email: '', password: '', role: '' });
  const [contacts, setContacts] = useState([{ type: 'discord', value: '' }]);
  const [captchaToken, setCaptchaToken] = useState('');
  const [agreedToRules, setAgreedToRules] = useState(false);
  const [googlePending, setGooglePending] = useState(null);
  const captchaRef = useRef();
  const { login } = useAuth();
  const navigate = useNavigate();

  const hasGoogleClient = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    setTab(params.get('tab') === 'register' ? 'register' : 'login');
  }, [params]);

  // ── GOOGLE LOGIN ──
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      try {
        const { data } = await API.post('/auth/google', {
          access_token: tokenResponse.access_token,
        });
        if (data.needs_completion) {
          // New user - show completion modal
          setGooglePending(data);
          setGoogleLoading(false);
          return;
        }
        // Existing user - log in directly
        login(data.token, data.user);
        toast.success(`Welcome back, ${data.user.username}!`);
        navigate(data.user.role === 'seller' || data.user.role === 'admin' ? '/dashboard' : '/browse');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Google login failed');
      } finally { setGoogleLoading(false); }
    },
    onError: () => { toast.error('Google login failed'); setGoogleLoading(false); },
  });

  const handleGoogleLogin = () => { if (!hasGoogleClient) return; setGoogleLoading(true); googleLogin(); };

  const handleGoogleComplete = (data) => {
    login(data.token, data.user);
    toast.success(`Welcome to RobloxMart, ${data.user.username}!`);
    setGooglePending(null);
    navigate(data.user.role === 'seller' ? '/dashboard' : '/browse');
  };

  // ── FORGOT PASSWORD ──
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return toast.error('Enter your email');
    setLoading(true);
    try {
      await API.post('/auth/forgot-password', { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset email');
    } finally { setLoading(false); }
  };

  // ── LOGIN ──
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) return toast.error('Please fill in all fields');
    setLoading(true);
    try {
      const { data } = await API.post('/auth/login', loginData);
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.username}!`);
      if (data.user.role === 'admin') navigate('/admin');
      else if (data.user.role === 'seller') navigate('/dashboard');
      else navigate('/browse');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  // ── REGISTER ──
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regData.role) return toast.error('Please select an account type (Buyer or Seller)');
    if (!captchaToken) return toast.error('Please complete the reCAPTCHA');
    if (!agreedToRules) return toast.error('You must agree to the Rules and Terms of Service');
    if (!regData.username || !regData.email || !regData.password) return toast.error('Please fill in all fields');
    if (regData.password.length < 6) return toast.error('Password must be at least 6 characters');
    if (regData.role === 'seller') {
      const validContacts = contacts.filter(c => c.value.trim());
      if (validContacts.length === 0) return toast.error('Sellers must provide at least one contact method');
    }
    setLoading(true);
    try {
      const payload = {
        ...regData,
        captcha_token: captchaToken,
        contact_methods: regData.role === 'seller' ? contacts.filter(c => c.value.trim()) : []
      };
      await API.post('/auth/signup', payload);
      toast.success('Account created! Please check your email to verify your account.');
      setTab('login');
      captchaRef.current?.reset();
      setCaptchaToken('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
      captchaRef.current?.reset();
      setCaptchaToken('');
    } finally { setLoading(false); }
  };

  const updateContact = (type, val) => {
    const existing = contacts.findIndex(c => c.type === type);
    if (existing >= 0) {
      const updated = [...contacts];
      if (!val.trim()) updated.splice(existing, 1);
      else updated[existing] = { type, value: val };
      setContacts(updated);
    } else if (val.trim()) {
      setContacts(prev => [...prev, { type, value: val }]);
    }
  };

  // ── FORGOT PASSWORD VIEW ──
  if (showForgot) {
    return (
      <div className="min-h-screen bg-rm-bg flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-scale-in">
          <div className="flex items-center gap-3 mb-8">
            <button onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(''); }}
              className="w-8 h-8 flex items-center justify-center text-zinc-600 hover:text-white border border-[#222] hover:border-[#333] rounded-sm transition-colors">
              <ArrowLeft size={15} />
            </button>
            <h2 className="font-rajdhani font-bold text-2xl uppercase tracking-wide">Reset Password</h2>
          </div>
          {forgotSent ? (
            <div className="bg-green-500/5 border border-green-500/20 rounded-sm p-6 text-center space-y-3">
              <Mail size={28} className="mx-auto text-green-500" />
              <h3 className="font-rajdhani font-bold text-lg text-white">Check Your Email</h3>
              <p className="text-sm text-zinc-500">If an account with that email exists, we've sent a password reset link.</p>
              <button onClick={() => { setShowForgot(false); setForgotSent(false); }} className="btn-primary px-6 py-2.5 text-sm rounded-sm mt-2">
                Back to Login
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="forgot-password-form">
              <p className="text-sm text-zinc-500">Enter your account email and we'll send you a reset link.</p>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-2">Email Address</label>
                <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                  placeholder="you@example.com" data-testid="forgot-email"
                  className="w-full bg-[#0a0a0a] border border-[#222] text-white placeholder:text-zinc-700 px-4 py-3 rounded-sm text-sm" />
              </div>
              <button type="submit" disabled={loading} data-testid="forgot-submit"
                className="w-full btn-primary py-3 rounded-sm text-sm disabled:opacity-50">
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Google Completion Modal */}
      {googlePending && (
        <GoogleCompleteModal
          pendingData={googlePending}
          onComplete={handleGoogleComplete}
          onCancel={() => setGooglePending(null)}
        />
      )}

      <div className="min-h-screen bg-[#080808] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <img src={LOGO} alt="RobloxMart" className="h-12 w-12 object-contain mb-3" />
            <h1 className="font-rajdhani font-bold text-2xl uppercase tracking-wide">
              ROBLOX<span className="text-rm-red">MART</span>
            </h1>
          </div>

          {/* Card */}
          <div className="bg-[#0d0d0d] border border-[#1e1e1e] rounded-sm overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-[#1e1e1e]">
              {['login', 'register'].map(t => (
                <button key={t} onClick={() => setTab(t)} data-testid={`auth-tab-${t}`}
                  className={`flex-1 py-4 font-rajdhani font-bold uppercase tracking-widest text-sm transition-all
                    ${tab === t ? 'text-rm-red border-b-2 border-rm-red -mb-[2px] bg-rm-red/5' : 'text-zinc-600 hover:text-white hover:bg-white/[0.02]'}`}>
                  {t === 'login' ? 'Sign In' : 'Register'}
                </button>
              ))}
            </div>

            <div className="p-6">
              {/* Google Login */}
              {hasGoogleClient && (
                <>
                  <GoogleButton loading={googleLoading} onClick={handleGoogleLogin}
                    text={tab === 'login' ? 'Continue with Google' : 'Register with Google'} />
                  <Divider />
                </>
              )}

              {/* LOGIN FORM */}
              {tab === 'login' && (
                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-2">Email</label>
                    <input type="email" data-testid="login-email" placeholder="you@example.com"
                      className="w-full bg-[#0a0a0a] border border-[#222] text-white placeholder:text-zinc-700 px-4 py-3 rounded-sm text-sm"
                      value={loginData.email} onChange={e => setLoginData({ ...loginData, email: e.target.value })} />
                  </div>
                  <div className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Password</label>
                      <button type="button" onClick={() => setShowForgot(true)} data-testid="forgot-password-link"
                        className="text-[10px] text-zinc-600 hover:text-rm-red transition-colors uppercase tracking-wider">
                        Forgot Password?
                      </button>
                    </div>
                    <input type={showPass ? 'text' : 'password'} data-testid="login-password" placeholder="••••••••"
                      className="w-full bg-[#0a0a0a] border border-[#222] text-white placeholder:text-zinc-700 px-4 py-3 rounded-sm text-sm pr-10"
                      value={loginData.password} onChange={e => setLoginData({ ...loginData, password: e.target.value })} />
                    <button type="button" onClick={() => setShowPass(!showPass)}
                      className="absolute right-3 bottom-3 text-zinc-600 hover:text-white">
                      {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  <button type="submit" disabled={loading} data-testid="login-submit"
                    className="w-full btn-primary py-3 rounded-sm text-sm mt-1 disabled:opacity-50">
                    {loading ? 'Signing In...' : 'Sign In'}
                  </button>
                  <p className="text-center text-xs text-zinc-700">
                    No account?{' '}
                    <button type="button" onClick={() => setTab('register')} className="text-rm-red hover:underline">Register now</button>
                  </p>
                </form>
              )}

              {/* REGISTER FORM */}
              {tab === 'register' && (
                <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-2">Username</label>
                    <input type="text" data-testid="reg-username" placeholder="your_username"
                      className="w-full bg-[#0a0a0a] border border-[#222] text-white placeholder:text-zinc-700 px-4 py-3 rounded-sm text-sm"
                      value={regData.username} onChange={e => setRegData({ ...regData, username: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-2">Email</label>
                    <input type="email" data-testid="reg-email" placeholder="you@example.com"
                      className="w-full bg-[#0a0a0a] border border-[#222] text-white placeholder:text-zinc-700 px-4 py-3 rounded-sm text-sm"
                      value={regData.email} onChange={e => setRegData({ ...regData, email: e.target.value })} />
                  </div>
                  <div className="relative">
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-2">Password</label>
                    <input type={showPass ? 'text' : 'password'} data-testid="reg-password" placeholder="Min 6 characters"
                      className="w-full bg-[#0a0a0a] border border-[#222] text-white placeholder:text-zinc-700 px-4 py-3 rounded-sm text-sm pr-10"
                      value={regData.password} onChange={e => setRegData({ ...regData, password: e.target.value })} />
                    <button type="button" onClick={() => setShowPass(!showPass)}
                      className="absolute right-3 bottom-3 text-zinc-600 hover:text-white">
                      {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>

                  {/* Role */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-2">
                      Account Type <span className="text-rm-red">*</span>
                    </label>
                    {!regData.role && (
                      <p className="text-[10px] text-amber-500/80 mb-2 flex items-center gap-1">
                        <span>▸</span> Please choose one to continue
                      </p>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      {['buyer', 'seller'].map(r => (
                        <button key={r} type="button" data-testid={`role-${r}`}
                          onClick={() => setRegData({ ...regData, role: r })}
                          className={`py-3 border rounded-sm font-rajdhani font-bold uppercase text-sm transition-all
                            ${regData.role === r
                              ? 'border-rm-red text-rm-red bg-rm-red/10'
                              : !regData.role
                                ? 'border-amber-500/40 text-zinc-500 hover:border-amber-500/70 hover:text-zinc-300'
                                : 'border-[#222] text-zinc-600 hover:border-[#333] hover:text-zinc-400'
                            }`}>
                          {r === 'buyer' ? 'Buyer' : 'Seller'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Seller contacts */}
                  {regData.role === 'seller' && (
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 block mb-1">Contact Methods</label>
                      <p className="text-[10px] text-amber-500/80 mb-3">Fill in at least one — buyers will use this to reach you</p>
                      <div className="space-y-2">
                        {[
                          { type: 'discord',  icon: MessageSquare, placeholder: 'YourName#1234 or discord.gg/...', required: true },
                          { type: 'telegram', icon: Send,          placeholder: '@YourTelegram',                   required: true },
                          { type: 'email',    icon: Mail,          placeholder: 'contact@email.com',               required: false },
                          { type: 'website',  icon: Globe,         placeholder: 'https://yoursite.com',            required: false },
                        ].map(({ type, icon: Icon, placeholder, required }) => (
                          <div key={type} className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5 w-28 shrink-0">
                              <Icon size={13} className={required ? 'text-rm-red/70' : 'text-zinc-600'} />
                              <span className={`text-xs capitalize ${required ? 'text-zinc-400' : 'text-zinc-600'}`}>{type}</span>
                              {required ? <span className="text-[9px] text-rm-red/60 font-bold">REQ</span> : <span className="text-[9px] text-zinc-700">opt</span>}
                            </div>
                            <input type="text" placeholder={placeholder}
                              value={contacts.find(c => c.type === type)?.value || ''}
                              onChange={e => updateContact(type, e.target.value)}
                              className="flex-1 bg-[#0a0a0a] border border-[#222] text-white placeholder:text-zinc-700 px-3 py-2 rounded-sm text-xs" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* reCAPTCHA */}
                  {SITE_KEY ? (
                    <div className="flex justify-center py-1">
                      <ReCAPTCHA ref={captchaRef} sitekey={SITE_KEY} theme="dark" onChange={setCaptchaToken} data-testid="recaptcha" />
                    </div>
                  ) : (
                    <div className="text-xs text-zinc-500 text-center py-2 bg-zinc-900/50 border border-zinc-700 rounded-sm" onClick={() => setCaptchaToken('dev-bypass')}>
                      reCAPTCHA not configured (dev mode — click to bypass)
                    </div>
                  )}

                  {/* Rules + TOS checkbox */}
                  <label className="flex items-start gap-3 cursor-pointer group" data-testid="rules-checkbox-label">
                    <div className="relative mt-0.5 shrink-0">
                      <input type="checkbox" checked={agreedToRules} onChange={e => setAgreedToRules(e.target.checked)}
                        className="sr-only" data-testid="rules-checkbox" />
                      <div className={`w-4 h-4 border rounded-sm transition-all flex items-center justify-center
                        ${agreedToRules ? 'bg-rm-red border-rm-red' : 'border-[#333] bg-[#0a0a0a] group-hover:border-[#444]'}`}>
                        {agreedToRules && <svg viewBox="0 0 10 8" fill="none" className="w-2.5 h-2.5"><path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </div>
                    </div>
                    <span className="text-[11px] text-zinc-600 leading-relaxed">
                      I have read and agree to the{' '}
                      <Link to="/rules" target="_blank" className="text-rm-red hover:underline">Platform Rules</Link>
                      {' '}and{' '}
                      <Link to="/tos" target="_blank" className="text-rm-red hover:underline">Terms of Service</Link>.
                      I understand RobloxMart does not process payments.
                    </span>
                  </label>

                  <button type="submit" disabled={loading || !captchaToken || !agreedToRules || !regData.role} data-testid="register-submit"
                    className="w-full btn-primary py-3 rounded-sm text-sm disabled:opacity-50">
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
